import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { GoogleIcon, CleverIcon } from '@/components/displays/CustomIcons';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/inputs/LanguageSelector';
import { Backdrop, CircularProgress } from '@mui/material';
import { AuthProviderType } from '@/types/auth';
import { useNotifications } from '@toolpad/core';
import { OnboardingCard } from '@/components/displays/OnboardingCard';
import { OnboardingContainer } from '@/components/displays/OnboardingContainer';

export default function SignUp(props: { onSignIn?: () => void; onSignUp?: (provider: AuthProviderType, email?: string) => Promise<{ error?: string }>; loading?: boolean }) {
    const { t, i18n } = useTranslation();
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [signingUp, setSigningUp] = React.useState<AuthProviderType | undefined>(undefined);

    const notifications = useNotifications();

    const validateInputs = () => {
        const email = document.getElementById('email') as HTMLInputElement;

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage(t('common.form.invalidEmail'));
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        return isValid;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (emailError) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const email = data.get('email') as string;

        if (props.onSignUp) {
            setSigningUp('credential');
            const result = await props.onSignUp('credential', email).finally(() => {
                setSigningUp(undefined);
            });

            if (result.error) {
                notifications.show(result.error, { severity: 'error', autoHideDuration: 5000 });
            }
        }
    };

    return (
        <>
            <OnboardingContainer direction='column' justifyContent='space-between'>
                <OnboardingCard variant='outlined' style={{ position: 'relative' }}>
                    <Stack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography component='h1' variant='h4' sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}>
                            {t('common.signUp')}
                        </Typography>
                        <LanguageSelector onChange={(locale) => i18n.changeLanguage(locale)} />
                    </Stack>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button fullWidth variant='outlined' onClick={async () => props.onSignUp && (await props.onSignUp('google'))} startIcon={<GoogleIcon />}>
                            {t('welcome.oauth.google')}
                        </Button>
                        <Button fullWidth variant='outlined' onClick={async () => props.onSignUp && (await props.onSignUp('clever'))} startIcon={<CleverIcon />}>
                            {t('welcome.oauth.clever')}
                        </Button>
                    </Box>
                    <Divider>
                        <Typography sx={{ color: 'text.secondary' }}>{t('welcome.or')}</Typography>
                    </Divider>
                    <Box
                        component='form'
                        onSubmit={handleSubmit}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <FormControl>
                            <FormLabel htmlFor='email'>{t('common.form.email')}</FormLabel>
                            <TextField
                                required
                                fullWidth
                                id='email'
                                placeholder='your@email.com'
                                name='email'
                                autoComplete='email'
                                variant='outlined'
                                error={emailError}
                                helperText={emailErrorMessage}
                                color={emailError ? 'error' : 'primary'}
                            />
                        </FormControl>
                        <FormControlLabel control={<Checkbox value='termsAndConditions' color='primary' required />} label={t('welcome.termsAndConditions')} />
                        <Button loading={signingUp === 'credential'} type='submit' fullWidth variant='contained' onClick={validateInputs} loadingIndicator={<CircularProgress size={16} />}>
                            {t('common.signUp')}
                        </Button>
                        <Typography sx={{ textAlign: 'center' }}>
                            {t('welcome.signUp.alreadyHaveAccount')}{' '}
                            <Link component='button' onClick={props.onSignIn} variant='body2' sx={{ verticalAlign: 'baseline' }}>
                                {t('common.signIn')}
                            </Link>
                        </Typography>
                    </Box>
                    <Backdrop style={{ position: 'absolute' }} open={!!props.loading}>
                        <CircularProgress color='inherit' />
                    </Backdrop>
                </OnboardingCard>
            </OnboardingContainer>
        </>
    );
}
